import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Shield, Menu, X, ChevronRight, LogOut, KeyRound, User as UserIcon, ChevronDown } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

function roleHome(role) {
  if (role === "parent") return "/dashboard/parent";
  if (role === "recruiter") return "/dashboard/recruiter";
  if (role === "super_admin" || role === "tester_admin") return "/dashboard/admin";
  return "/";
}

// Rankings is gated to the platform owner only.
const RANKINGS_OWNER_EMAIL = "brandon@omnisportsgroup.us";
const canSeeRankings = (user) =>
  !!user && (user.email || "").toLowerCase() === RANKINGS_OWNER_EMAIL;

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [userMenuOpen]);

  // Hide nav on full-screen report
  if (loc.pathname.startsWith("/evaluations/")) return null;

  // Smooth scroll to the Landing "How It Works" anchor; navigate to "/" first if needed.
  const goHowItWorks = (e) => {
    e.preventDefault();
    setOpen(false);
    const scrollNow = () => {
      const el = document.getElementById("how-it-works");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (loc.pathname === "/") {
      scrollNow();
    } else {
      nav("/#how-it-works");
      // Give the route a tick to mount, then scroll.
      setTimeout(scrollNow, 80);
    }
  };

  const showRankings = canSeeRankings(user);
  const links = [
    { to: "/", label: "Home" },
    { type: "how-it-works", label: "How It Works" },
    { to: "/about", label: "About" },
    ...(showRankings ? [{ to: "/rankings", label: "Rankings" }] : []),
    { to: "/pricing", label: "Pricing" },
    user?.role === "recruiter"
      ? { to: "/dashboard/recruiter", label: "Recruiter Intel" }
      : { to: "/for-colleges", label: "For Colleges", highlight: true },
  ];

  return (
    <header
      className="sticky top-0 z-50 glass border-b border-white/5"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <div className="w-9 h-9 rounded-md bg-electric flex items-center justify-center shadow-[0_0_18px_rgba(0,229,255,0.45)]">
            <Shield size={18} className="text-black" strokeWidth={2.4} />
          </div>
          <div className="leading-none">
            <div className="font-display text-xl font-black tracking-[0.18em] text-white">
              VERIFIED
            </div>
            <div className="text-[9px] tracking-[0.3em] text-white/40 uppercase">
              Powered by OmniSportsGroup
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => {
            if (l.type === "how-it-works") {
              return (
                <a
                  key="how-it-works"
                  href="/#how-it-works"
                  onClick={goHowItWorks}
                  className="px-3 py-2 text-sm font-medium rounded-md transition-colors text-white/70 hover:text-white"
                  data-testid="nav-link-how-it-works"
                >
                  {l.label}
                </a>
              );
            }
            return (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  l.highlight
                    ? "text-electric hover:text-white border border-electric/30 hover:border-electric/60 bg-electric/5"
                    : "text-white/70 hover:text-white"
                }`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {user ? (
            <>
              <NotificationBell />
              <Link
                to={roleHome(user.role)}
                className="btn-ghost text-xs"
                data-testid="nav-dashboard-btn"
              >
                Dashboard <ChevronRight size={14} />
              </Link>
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-md border border-white/10 hover:border-electric/40 text-white/70 hover:text-white transition-colors"
                  data-testid="user-menu-trigger"
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                >
                  <div className="w-6 h-6 rounded-md bg-electric/15 border border-electric/30 flex items-center justify-center text-electric text-[10px] font-display font-black uppercase">
                    {(user.name || user.email || "?").trim().charAt(0)}
                  </div>
                  <ChevronDown size={12} className={`transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {userMenuOpen && (
                  <div
                    className="absolute right-0 mt-2 w-64 bento-card p-0 z-[60] shadow-[0_10px_40px_rgba(0,0,0,0.6)]"
                    data-testid="user-menu-dropdown"
                    role="menu"
                  >
                    <div className="px-4 py-3 border-b border-white/5">
                      <div className="text-sm font-display font-bold text-white truncate" data-testid="user-menu-name">
                        {user.name || "—"}
                      </div>
                      <div className="text-[11px] text-white/40 truncate font-mono" data-testid="user-menu-email">{user.email}</div>
                      <div className="text-[10px] uppercase tracking-widest text-electric/70 font-display font-bold mt-1.5">{user.role?.replace(/_/g, " ")}</div>
                    </div>
                    <Link
                      to={roleHome(user.role)}
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-xs text-white/80 hover:text-white hover:bg-white/[0.03] transition-colors"
                      role="menuitem"
                      data-testid="user-menu-dashboard"
                    >
                      <UserIcon size={13} /> My Dashboard
                    </Link>
                    <Link
                      to="/auth/change-password"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-xs text-white/80 hover:text-white hover:bg-white/[0.03] transition-colors border-t border-white/5"
                      role="menuitem"
                      data-testid="user-menu-change-password"
                    >
                      <KeyRound size={13} /> Change Password
                    </Link>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        logout().then(() => nav("/"));
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-rose-300 hover:text-rose-200 hover:bg-rose-400/[0.05] transition-colors border-t border-white/5"
                      role="menuitem"
                      data-testid="user-menu-logout"
                    >
                      <LogOut size={13} /> Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-white/70 hover:text-white px-3 py-2"
                data-testid="nav-login-btn"
              >
                Log in
              </Link>
              <Link to="/register" className="btn-primary text-xs" data-testid="nav-getverified-btn">
                Get VERIFIED
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/5 px-4 py-4 bg-black/95">
          {links.map((l) => {
            if (l.type === "how-it-works") {
              return (
                <a
                  key="how-it-works-m"
                  href="/#how-it-works"
                  onClick={goHowItWorks}
                  className="block py-2 text-white/80"
                  data-testid="mobile-nav-how-it-works"
                >
                  {l.label}
                </a>
              );
            }
            return (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="block py-2 text-white/80"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            );
          })}
          <div className="border-t border-white/10 mt-3 pt-3 flex flex-col gap-2">
            {user ? (
              <>
                <Link
                  to={roleHome(user.role)}
                  onClick={() => setOpen(false)}
                  className="btn-ghost text-xs justify-center"
                >
                  Dashboard
                </Link>
                <Link
                  to="/auth/change-password"
                  onClick={() => setOpen(false)}
                  className="btn-ghost text-xs justify-center"
                  data-testid="mobile-change-password"
                >
                  <KeyRound size={12} /> Change Password
                </Link>
                <button
                  onClick={() => {
                    logout().then(() => nav("/"));
                    setOpen(false);
                  }}
                  className="btn-ghost text-xs justify-center text-rose-300 border-rose-400/25 hover:border-rose-400/60"
                >
                  <LogOut size={12} /> Log out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost text-xs justify-center">
                  Log in
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary text-xs justify-center">
                  Get VERIFIED
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
